import CONFIG from "config/config";
import { authorization } from "module/auth";
import type {
  OnboardResponse,
  InitiateChargeRequest,
  InitiateChargeResponse,
  VerifyChargeResponse,
  PreviewBreakdownRequest,
  PreviewBreakdownResponse,
  CreateBillingProfileInput,
  TeamCardsResponse,
  AddTeamCardInput,
} from "./types";
import type { ReviewOfferEligibilityResponse } from "campaign-builder/channels/directmail/adm/order-details/reviewOffer/types";

function getBillingBase(): string {
  return `${CONFIG.API_URL}/billing`;
}

function getHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-api-key": authorization.getAPIKey(),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.message || `Request failed with status ${response.status}`) as Error & {
      code?: string;
      funding?: string;
    };
    // Charge-initiate typed errors use an `error` discriminator (e.g.
    // BILLING_PROFILE_REQUIRED, CURRENCY_MISMATCH); card-setup errors use `code`.
    const discriminator = body.code ?? body.error;
    err.code = typeof discriminator === "string" ? discriminator : undefined;
    // Card-save rejections name the card's funding type (NON_CREDIT_CARD_NOT_ALLOWED).
    err.funding = typeof body.funding === "string" ? body.funding : undefined;
    throw err;
  }
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text);
}

// ── Onboard ────────────────────────────────────────────────────────────────

export async function billingOnboard(): Promise<OnboardResponse> {
  const response = await fetch(`${getBillingBase()}/onboard`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse<OnboardResponse>(response);
}

// ── Card Setup (add a card without charging) ─────────────────────────────

export async function createSetupIntent(): Promise<{ clientSecret: string }> {
  const response = await fetch(`${getBillingBase()}/payment-methods/setup`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse<{ clientSecret: string }>(response);
}

export async function addPaymentMethod(data: { stripePaymentMethodId: string; setAsPrimary?: boolean }): Promise<void> {
  const response = await fetch(`${getBillingBase()}/payment-methods`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  await handleResponse<void>(response);
}

// ── Master Account team cards ──────────────────────────────────────────────
// A Master Account team pays with its own cards plus the organization cards
// authorized to it; they live on the team's / organization's Stripe customer,
// never on the payer's per-user one, so checkout reads and adds through the
// team-scoped endpoints.

export async function listTeamCards(): Promise<TeamCardsResponse> {
  const response = await fetch(`${getBillingBase()}/team/payment-methods`, {
    headers: getHeaders(),
  });
  return handleResponse<TeamCardsResponse>(response);
}

export async function createTeamCardSetupIntent(): Promise<{ clientSecret: string }> {
  const response = await fetch(`${getBillingBase()}/team/payment-methods/setup`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse<{ clientSecret: string }>(response);
}

export async function addTeamCard(data: AddTeamCardInput): Promise<void> {
  const response = await fetch(`${getBillingBase()}/team/payment-methods`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  await handleResponse<void>(response);
}

// ── Billing Address ───────────────────────────────────────────────────────
// Read-only from the client. `PUT /billing/address` was retired by oppizi-api
// (feat(billing): client-facing Billing Profile CRUD, 2026-08-21): the account
// default address is now the billing profile / Xero mirror, and the card's own
// address rides on the Stripe PaymentMethod via billing_details (PAD-32909).
// The default still arrives on `POST /billing/onboard` as defaultBillingAddress.

// ── Charges ───────────────────────────────────────────────────────────────

export async function initiateCharge(data: InitiateChargeRequest): Promise<InitiateChargeResponse> {
  const response = await fetch(`${getBillingBase()}/charges/initiate`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<InitiateChargeResponse>(response);
}

export async function verifyCharge(chargeUUID: string): Promise<VerifyChargeResponse> {
  const response = await fetch(`${getBillingBase()}/charges/${chargeUUID}/verify`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse<VerifyChargeResponse>(response);
}

// ── Review-for-discount offer (HUB-453, epic HUB-440) ───────────────────────
// GET /billing/checkout/review-offer/eligibility — platform-session auth,
// client resolved server-side from req.user.locked_client. No params.
// Always HTTP 200 (fail-closed via `reason`), per contract v1.

export async function getReviewOfferEligibility(): Promise<ReviewOfferEligibilityResponse> {
  const response = await fetch(`${getBillingBase()}/checkout/review-offer/eligibility`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse<ReviewOfferEligibilityResponse>(response);
}

// ── Review-offer funnel stages (HUB-477, epic HUB-440) ─────────────────────
// POST /billing/checkout/review-offer/funnel-stage — platform-session auth,
// client resolved server-side from req.user.locked_client (there is
// deliberately no client_id in the body). Persists the funnel stages we
// already report to PostHog so oppizi-api can fire a sequence trigger at
// each one. Fire-and-forget: always 202 on success, and every failure is
// swallowed — this is bookkeeping and must never surface in the checkout
// drawer.
//
// "Swallowed" means *silent for the user*, not invisible to us. `fetch` only
// rejects on a network-level failure, so a rejected stage (4xx/5xx) would
// otherwise be indistinguishable from a persisted one — and the stage, plus
// the sequence trigger that hangs off it, would be silently lost forever
// (there is no retry: the drawer moves on). So the response goes through the
// same `handleResponse` gate as every other call in this module, and the
// resulting error is logged rather than discarded — the same
// `console.error("[reviewOffer] …")` channel useReviewOfferState.ts uses for
// its own user-invisible failures.

export async function postReviewOfferFunnelStage(payload: {
  stage: string;
  campaign_id: string | number;
}): Promise<void> {
  try {
    const response = await fetch(`${getBillingBase()}/checkout/review-offer/funnel-stage`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
      // The last stages of this funnel fire as the drawer closes / the user
      // navigates away; without keepalive the browser cancels them in flight.
      keepalive: true,
    });
    await handleResponse<void>(response);
  } catch (error) {
    // Never rethrown: the checkout drawer must not break over a funnel row.
    // eslint-disable-next-line no-console
    console.error("[reviewOffer] funnel-stage post failed", payload.stage, error);
  }
}

// ── Billing Profile (Master Account) ───────────────────────────────────────

// Create a billing profile from the inline Set-up drawer. A client-created
// profile is DB-only; ops adopt it into Xero later.
export async function createBillingProfile(input: CreateBillingProfileInput): Promise<void> {
  const response = await fetch(`${getBillingBase()}/profiles`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(input),
  });
  await handleResponse<void>(response);
}

// ── Server-Authoritative Breakdown ─────────────────────────────────────────

export async function previewBreakdown(data: PreviewBreakdownRequest): Promise<PreviewBreakdownResponse> {
  const response = await fetch(`${getBillingBase()}/breakdown/preview`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<PreviewBreakdownResponse>(response);
}
